import React, { useEffect, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import Chip from "@material-ui/core/Chip";
import OutlinedInput from "@material-ui/core/OutlinedInput";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles(theme => ({
	chips: {
		display: "flex",
		flexWrap: "wrap",
	},
	chip: {
		margin: 2,
	},
	compactChips: {
		position: "relative",
		display: "flex",
		alignItems: "center",
		gap: 4,
		minWidth: 0,
		width: "100%",
		whiteSpace: "nowrap",
	},
	compactChip: { flexShrink: 0, maxWidth: "100%" },
	measureChips: {
		position: "absolute", visibility: "hidden", pointerEvents: "none",
		display: "flex", gap: 4, width: "max-content", left: 0, top: 0,
	},
}));

// Find the largest prefix that fits alongside the count of hidden queues.
export const fitQueueChips = (width, chipWidths, counterWidths, gap = 4) => {
	const count = chipWidths.length;
	if (!count || width <= 0) return 0;
	const total = chipWidths.reduce((sum, chip) => sum + chip, 0) + gap * (count - 1);
	if (total <= width) return count;
	for (let visible = count - 1; visible > 0; visible--) {
		const used = chipWidths.slice(0, visible).reduce((sum, chip) => sum + chip, 0) +
			counterWidths[count - visible] + gap * visible;
		if (used <= width) return visible;
	}
	// Preserve a truncated first name when there is room beside the counter.
	return width >= (counterWidths[count - 1] || 0) + gap + 60 ? 1 : 0;
};

const CompactQueueChips = ({ items, classes }) => {
	const containerRef = useRef(null);
	const measureRef = useRef(null);
	const [visibleCount, setVisibleCount] = useState(0);
	useEffect(() => {
		const container = containerRef.current;
		const measure = measureRef.current;
		if (!container || !measure) return undefined;
		let active = true;
		const update = () => {
			if (!active) return;
			const width = container.getBoundingClientRect().width;
			if (!width) return;
			const chips = [...measure.querySelectorAll("[data-measure-chip]")].map(el => el.getBoundingClientRect().width);
			const counters = [0, ...[...measure.querySelectorAll("[data-measure-counter]")].map(el => el.getBoundingClientRect().width)];
			setVisibleCount(fitQueueChips(width, chips, counters));
		};
		update();
		const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(update) : null;
		if (observer) { observer.observe(container); observer.observe(measure); }
		window.addEventListener("resize", update);
		if (document.fonts?.ready) document.fonts.ready.then(update);
		return () => { active = false; if (observer) observer.disconnect(); window.removeEventListener("resize", update); };
	}, [items]);
	const shown = Math.min(visibleCount, items.length);
	const hidden = items.length - shown;
	const renderChip = item => <Chip className={classes.compactChip} variant="outlined" label={item.name}
		style={{ backgroundColor: item.color }} />;
	return (
		<div className={classes.compactChips} ref={containerRef}>
			{items.slice(0, shown).map((item, index) => <React.Fragment key={item.id}>
				{index === 0 && hidden > 0
					? <span style={{ minWidth: 0, overflow: "hidden", display: "inline-flex" }}>{renderChip(item)}</span>
					: renderChip(item)}
			</React.Fragment>)}
			{hidden > 0 && <Chip className={classes.compactChip} size="small" label={`+${hidden}`}
				title={`${hidden} fila(s) selecionada(s) oculta(s)`} />}
			<div className={classes.measureChips} ref={measureRef} aria-hidden="true">
				{items.map(item => <span key={item.id} data-measure-chip>{renderChip(item)}</span>)}
				{items.map((_, index) => <span key={index} data-measure-counter><Chip size="small" label={`+${index + 1}`} /></span>)}
			</div>
		</div>
	);
};

const QueueSelect = ({ selectedQueueIds, onChange, multiple = true, title = i18n.t("queueSelect.inputLabel"), showNoQueue = false }) => {
	const classes = useStyles();
	const [queues, setQueues] = useState([]);
	const labelRef = useRef(null);
	const labelIdRef = useRef(`queue-select-label-${Math.random().toString(36).slice(2, 9)}`);
	const [labelWidth, setLabelWidth] = useState(0);
	const [menuOpen, setMenuOpen] = useState(false);

	useEffect(() => {

		fetchQueues();

	}, []);

	const fetchQueues = async () => {
		try {
			const { data } = await api.get("/queue");
			setQueues(data);
		} catch (err) {
			toastError(err);
		}
	}

	const handleChange = e => {
		onChange(e.target.value);
	};

	const hasValue = multiple
		? Array.isArray(selectedQueueIds) && selectedQueueIds.length > 0
		: selectedQueueIds !== null && selectedQueueIds !== undefined && selectedQueueIds !== "";
	const shouldShrink = hasValue || menuOpen;

	useEffect(() => {
		if (labelRef.current) {
			setLabelWidth(labelRef.current.offsetWidth);
		}
	}, [title]);

	return (
		<div >
			<FormControl fullWidth margin="dense" variant="outlined">
				<InputLabel
					id={labelIdRef.current}
					ref={labelRef}
					shrink={shouldShrink}
				>
					{title}
				</InputLabel>
				<Select
					label={title}
					multiple={multiple}
					labelId={labelIdRef.current}
					value={selectedQueueIds}
					onChange={handleChange}
					onOpen={() => setMenuOpen(true)}
					onClose={() => setMenuOpen(false)}
					input={
						<OutlinedInput
							labelWidth={labelWidth}
							notched={shouldShrink}
						/>
					}
					MenuProps={{
						anchorOrigin: {
							vertical: "bottom",
							horizontal: "left",
						},
						transformOrigin: {
							vertical: "top",
							horizontal: "left",
						},
						getContentAnchorEl: null,
					}}

					renderValue={selected => {
						if (multiple && Array.isArray(selected) && selected.length) {
							const items = selected.map(id => id === -1
								? { id, name: "Sem Fila", color: "#cccccc" }
								: queues.find(queue => queue.id === id)).filter(Boolean);
							return <CompactQueueChips items={items} classes={classes} />;
						}
						return (
							<div className={classes.chips}>
								{selected?.length > 0 && multiple ? (
									selected.map(id => {
										if (id === -1) {
											return (
												<Chip
													key={id}
													style={{ backgroundColor: "#cccccc" }}
													variant="outlined"
													label="Sem Fila"
													className={classes.chip}
												/>
											);
										}
										const queue = queues.find(q => q.id === id);
										return queue ? (
											<Chip
												key={id}
												style={{ backgroundColor: queue.color }}
												variant="outlined"
												label={queue.name}
												className={classes.chip}
											/>
										) : null;
									})

								) :
									(
										selected === -1 ? (
											<Chip
												key={selected}
												variant="outlined"
												style={{ backgroundColor: "#cccccc" }}
												label="Sem Fila"
												className={classes.chip}
											/>
										) : (
											<Chip
												key={selected}
												variant="outlined"
												style={{ backgroundColor: queues.find(q => q.id === selected)?.color }}
												label={queues.find(q => q.id === selected)?.name}
												className={classes.chip}
											/>
										)
									)
								}

							</div>
						)
					}}
				>
					{!multiple && <MenuItem value={null}>Nenhum</MenuItem>}
					{showNoQueue && (
						<MenuItem key={-1} value={-1}>
							Sem Fila
						</MenuItem>
					)}
					{queues.map(queue => (
						<MenuItem key={queue.id} value={queue.id}>
							{queue.name}
						</MenuItem>
					))}
				</Select>
			</FormControl>
		</div>
	);
};

export default QueueSelect;
